/**
 * @file Prefab declarations: the only file that knows what an entity looks like.
 *
 * Registration is table-driven — every enemy archetype, every weapon's round and
 * every powerup gets its prefab generated from the content tables. Adding a row
 * to `ENEMIES`, `WEAPONS` or `POWERUPS` therefore produces a working mesh with
 * no edit here (GDD §23).
 *
 * Art direction is neon cyber-arena: matte dark bodies with a strong emissive
 * channel, which is what the bloom pass turns into glow.
 */

import * as THREE from 'three';
import { PALETTE } from '../config/Palette.js';
import { ENEMIES } from '../config/Enemies.js';
import { WEAPONS } from '../config/Weapons.js';
import { POWERUPS, WEAPON_PICKUPS } from '../config/Powerups.js';
import { buildExitArrow } from './ExitDirectionIndicator.js';

/** Static prefab keys. Dynamic ones are derived from the content tables. */
export const Prefab = Object.freeze({
  PLAYER: 'player',
  /** @param {string} key Enemy archetype key. @returns {string} */
  enemy: (key) => `enemy:${key}`,
  /** @param {string} key Powerup key. @returns {string} */
  pickup: (key) => `pickup:${key}`,
  /** @param {string} key Weapon archetype key. @returns {string} */
  weapon: (key) => `weapon:${key}`
});

/**
 * Name of the empty group every armed body carries, into which
 * {@link ../systems/HeldWeaponSystem.js} drops the model of whatever that body
 * is holding. Built into the prefab rather than attached at runtime so a
 * recycled mesh always comes back out of the pool with its socket intact.
 */
export const WEAPON_MOUNT = 'weapon-mount';

/**
 * Name of the fallback nub shown in place of a weapon when its model is
 * missing. Unarmed bodies keep the socket empty.
 */
export const WEAPON_MOUNT_FALLBACK = 'weapon-mount:fallback';

/**
 * Registers every prefab on a factory. Call once during loading.
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @param {import('../config/GameConfig.js').GameConfig} config Game config.
 * @param {import('./WeaponModels.js').WeaponModelLibrary} [weaponModels] Source
 *   of authored weapon models. Omitted (tests, headless) means every weapon
 *   falls back to its procedural block.
 * @param {import('./CharacterModels.js').CharacterModelLibrary} [characterModels]
 *   Source of authored character bodies. Omitted (tests, headless) means the
 *   player falls back to its procedural capsule.
 * @returns {void}
 */
export function registerPrefabs(factory, config, weaponModels, characterModels) {
  registerPlayer(factory, config, characterModels);
  registerEnemies(factory);
  registerProjectiles(factory);
  registerLooseWeapons(factory, weaponModels);
  registerPickups(factory);
}

/**
 * Builds the emissive body material every neon entity shares the recipe for.
 * @param {number} color Base colour.
 * @param {number} emissiveIntensity How hard it feeds the bloom pass.
 * @returns {THREE.MeshStandardMaterial}
 */
function neonBody(color, emissiveIntensity) {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity,
    roughness: 0.36,
    metalness: 0.18
  });
}

/**
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @param {import('../config/GameConfig.js').GameConfig} config Game config.
 * @param {import('./CharacterModels.js').CharacterModelLibrary} [characterModels]
 *   Source of the authored player body.
 * @returns {void}
 */
function registerPlayer(factory, config, characterModels) {
  const { player } = config;

  factory.definePrefab(Prefab.PLAYER, {
    model: () => characterModels?.create('player') ?? null,
    geometry: () => new THREE.CapsuleGeometry(player.radius, player.height * 0.55, 6, 16),
    material: () => neonBody(player.color, 0.55),
    // Never prewarmed: the prefab is declared before the model/skin finish
    // downloading (Game constructor runs before LoadingState), and prewarming
    // would build — and then permanently pool — the procedural fallback
    // (same reasoning as the loose-weapon prefabs below).
    prewarm: 0,
    decorate: (mesh, meshFactory) => {
      // The gun the player is actually holding, or a stub only when that gun's
      // authored model is unavailable. Unarmed means an empty socket. The
      // authored character body ships its own socket already riding the hand
      // bone (see CharacterModels.js) — only the procedural capsule fallback
      // needs this generic chest-level one.
      if (!mesh.getObjectByName(WEAPON_MOUNT)) {
        mesh.add(
          buildWeaponMount(meshFactory, {
            position: [0, 0.05, 0.58],
            fallbackColor: player.barrelColor
          })
        );
      }

      // Halo ring on the floor: keeps the player readable under a pile of enemies.
      const ring = new THREE.Mesh(
        meshFactory.geometry(
          'player:ring',
          () => new THREE.RingGeometry(player.radius * 1.5, player.radius * 1.85, 24)
        ),
        meshFactory.material(
          'player:ring',
          () =>
            new THREE.MeshBasicMaterial({
              color: PALETTE.player,
              transparent: true,
              opacity: 0.55,
              side: THREE.DoubleSide,
              depthWrite: false,
              toneMapped: false
            })
        )
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = -player.height * 0.55 + 0.03;
      ring.castShadow = false;
      mesh.add(ring);

      // Compass toward the level exit. Sits low and off to the side so it is
      // never mistaken for the barrel's aim line (issue #16).
      mesh.add(buildExitArrow(meshFactory, player));
    }
  });
}

/**
 * Builds the socket an armed body holds its weapon in.
 *
 * The group sits down the aim line, along +Z — the axis the whole game orients
 * by — so a model dropped straight into it already points where its holder is
 * aiming. {@link ../systems/HeldWeaponSystem.js} owns what goes inside.
 *
 * @param {import('./MeshFactory.js').MeshFactory} meshFactory Shared factory.
 * @param {object} options Socket setup.
 * @param {[number, number, number]} options.position Local grip position.
 * @param {number} [options.fallbackColor] Colour of a nub to show while the
 *   equipped weapon's model is missing. Omit for bodies that should simply
 *   show nothing when art is unavailable.
 * @returns {THREE.Group}
 */
function buildWeaponMount(meshFactory, { position, fallbackColor }) {
  const mount = new THREE.Group();
  mount.name = WEAPON_MOUNT;
  mount.position.set(...position);

  if (fallbackColor === undefined) return mount;

  const fallback = new THREE.Mesh(
    meshFactory.geometry('weapon-mount:fallback', () => new THREE.BoxGeometry(0.2, 0.2, 1.05)),
    meshFactory.material(
      `weapon-mount:fallback:${fallbackColor}`,
      () =>
        new THREE.MeshStandardMaterial({
          color: fallbackColor,
          emissive: fallbackColor,
          emissiveIntensity: 1.4,
          roughness: 0.25,
          metalness: 0.6
        })
    )
  );
  fallback.name = WEAPON_MOUNT_FALLBACK;
  fallback.visible = false;
  fallback.castShadow = true;
  mount.add(fallback);

  return mount;
}

/**
 * One prefab per enemy archetype, sized and coloured from the table.
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @returns {void}
 */
function registerEnemies(factory) {
  for (const [key, archetype] of Object.entries(ENEMIES)) {
    const prefabKey = Prefab.enemy(key);

    factory.definePrefab(prefabKey, {
      geometry: () =>
        new THREE.CapsuleGeometry(archetype.radius, archetype.height * 0.5, 5, 12),
      material: () => neonBody(archetype.color, 0.42),
      prewarm: key === 'grunt' ? 24 : 8,
      decorate: (mesh, meshFactory) => {
        // Forward-facing core: the only way to read facing from directly above.
        const core = new THREE.Mesh(
          meshFactory.geometry(
            `${prefabKey}:core`,
            () => new THREE.SphereGeometry(archetype.radius * 0.28, 8, 6)
          ),
          meshFactory.material(
            `${prefabKey}:core`,
            () =>
              new THREE.MeshBasicMaterial({
                color: PALETTE.enemyCore,
                toneMapped: false
              })
          )
        );
        core.castShadow = false;
        core.position.set(0, archetype.height * 0.16, archetype.radius * 0.9);
        mesh.add(core);

        // Armed archetypes show the gun they actually carry — and, once it is
        // dropped, the same model keeps lying on the floor. Melee archetypes
        // get the empty socket and stay empty-handed.
        mesh.add(
          buildWeaponMount(meshFactory, {
            position: [0, archetype.height * 0.04, archetype.radius * 0.95]
          })
        );
      }
    });
  }
}

/**
 * One prefab per weapon round. The cylinder is laid along Z so a bullet can be
 * oriented straight from its travel direction.
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @returns {void}
 */
function registerProjectiles(factory) {
  /** @type {Set<string>} Two archetypes may share one round (player/enemy variants). */
  const defined = new Set();

  for (const archetype of Object.values(WEAPONS)) {
    // Melee weapons have no projectile.
    if (archetype.type === 'melee' || !archetype.projectile) continue;
    const { prefab, color, radius, length } = archetype.projectile;
    if (defined.has(prefab)) continue;
    defined.add(prefab);

    factory.definePrefab(prefab, {
      geometry: () => {
        const geometry = new THREE.CylinderGeometry(radius, radius, length, 6, 1);
        geometry.rotateX(Math.PI / 2);
        return geometry;
      },
      // Basic + toneMapped:false keeps rounds at full intensity for the bloom.
      material: () => new THREE.MeshBasicMaterial({ color, toneMapped: false }),
      castShadow: false,
      // Streaming weapons sweep a cone instead of spawning rounds, so pooling
      // meshes for them would allocate a pile of geometry nothing ever draws.
      prewarm: archetype.streaming ? 0 : 48
    });
  }
}

/**
 * One prefab per weapon, used by a weapon that has left its holder — mid-throw
 * or lying on the floor. Laid along Z so the render system can orient it from
 * its travel direction.
 *
 * The real model is preferred; the procedural block behind it is kept as the
 * fallback for a weapon with no art, and is tinted with the round it fires so
 * the player can still tell at a glance what is on the ground.
 *
 * Nothing is prewarmed: these prefabs are declared before the models finish
 * downloading, and building a mesh early would bake the fallback in.
 *
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @param {import('./WeaponModels.js').WeaponModelLibrary} [weaponModels] Source
 *   of authored weapon models.
 * @returns {void}
 */
function registerLooseWeapons(factory, weaponModels) {
  for (const [key, archetype] of Object.entries(WEAPONS)) {
    factory.definePrefab(Prefab.weapon(key), {
      model: () => weaponModels?.create(key) ?? null,
      geometry: () => new THREE.BoxGeometry(0.2, 0.2, 0.82),
      material: () => neonBody(archetype.projectile.color, 1.1),
      prewarm: 0,
      decorate: (mesh, meshFactory) => {
        if (weaponModels?.has(key)) return;

        // Stubby grip: enough silhouette to read as a weapon rather than a crate.
        const grip = new THREE.Mesh(
          meshFactory.geometry('weapon:grip', () => new THREE.BoxGeometry(0.16, 0.3, 0.18)),
          meshFactory.material(`weapon:grip:${key}`, () =>
            neonBody(archetype.projectile.color, 0.5)
          )
        );
        grip.position.set(0, -0.18, -0.24);
        mesh.add(grip);
      }
    });
  }
}

/**
 * One prefab per powerup: a slowly spinning octahedron in the powerup's colour.
 * Weapons that never appear in the random drop table are registered too, since
 * an enemy can drop the gun it carries (GDD §Fase 3 — weapon swap).
 * @param {import('./MeshFactory.js').MeshFactory} factory Target factory.
 * @returns {void}
 */
function registerPickups(factory) {
  const droppable = [...Object.entries(POWERUPS), ...Object.entries(WEAPON_PICKUPS)];

  for (const [key, definition] of droppable) {
    factory.definePrefab(Prefab.pickup(key), {
      geometry: () => new THREE.OctahedronGeometry(0.42, 0),
      material: () =>
        new THREE.MeshStandardMaterial({
          color: definition.color,
          emissive: definition.color,
          emissiveIntensity: 1.6,
          roughness: 0.2,
          metalness: 0.4
        }),
      prewarm: 4,
      decorate: (mesh, meshFactory) => {
        const halo = new THREE.Mesh(
          meshFactory.geometry('pickup:halo', () => new THREE.RingGeometry(0.6, 0.78, 20)),
          meshFactory.material(`pickup:halo:${key}`, () =>
            new THREE.MeshBasicMaterial({
              color: definition.color,
              transparent: true,
              opacity: 0.5,
              side: THREE.DoubleSide,
              depthWrite: false,
              toneMapped: false
            })
          )
        );
        halo.rotation.x = -Math.PI / 2;
        halo.position.y = -0.85;
        halo.castShadow = false;
        mesh.add(halo);
      }
    });
  }
}
